import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const HomeContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f2f5;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const Title = styled.h2`
  color: #1a1a1a;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const UserInfo = styled.span`
  font-size: 16px;
  font-weight: normal;
  color: #666;
  &::before {
    content: '|';
    margin-right: 15px;
    color: #ddd;
  }
`;

const LogoutButton = styled.button`
  background-color: transparent;
  color: #dc3545;
  border: 1px solid #dc3545;
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    background-color: #dc3545;
    color: white;
  }
`;

const MainContent = styled.div`
  display: flex;
  gap: 20px;
  height: calc(100vh - 100px);  // 减去头部的高度
  padding: 20px;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    height: auto;
  }
`;

const Section = styled.div`
  background: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const InputSection = styled.div`
  flex: 1;
  min-width: 0;  // 防止 flex 子项溢出
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  overflow-y: auto;  // 添加垂直滚动
  overflow-x: hidden;  // 隐藏水平滚动条
  max-height: 100%;  // 限制最大高度
`;

const LogSection = styled.div`
  width: 300px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  
  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const LogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 15px 10px;
  border-bottom: 1px solid #eee;
`;

const LogTitle = styled.h3`
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #dc3545;
  }
`;

const LogContent = styled.div`
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  max-height: calc(100vh - 200px);  // 限制最大高度
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  max-width: 600px;
  width: 100%;
  margin: 100px auto;  // 添加上下边距使其居中
  padding: 30px;  // 增加内边距
  background: #ffffff;  // 添加白色背景
  border-radius: 8px;  // 圆角
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);  // 添加阴影
`;

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 15px;
  max-width: 400px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #1a1a1a;
  white-space: nowrap;
  font-size: 15px;  // 增加字体大小
`;

const Input = styled.input`
  flex: 1;
  height: 44px;  // 增加高度
  padding: 0 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 15px;
  transition: all 0.3s ease;
  background-color: #f8f9fa;  // 添加浅灰色背景

  &:focus {
    border-color: #4a90e2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
    outline: none;
    background-color: #ffffff;  // 聚焦时变为白色
  }

  &::placeholder {
    color: #aaa;
  }
`;

const SubmitButton = styled.button`
  height: 44px;  // 增加高度匹配输入框
  padding: 0 30px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.3s ease;
  min-width: 120px;  // 设置最小宽度

  &:hover:not(:disabled) {
    background-color: #357abd;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);  // 悬停时添加阴影
  }

  &:disabled {
    background-color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const LogEntry = styled.div`
  padding: 8px 12px;
  margin-bottom: 6px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  color: #333;
  line-height: 1.4;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background-color: #ffe6e6;
  padding: 10px 15px;
  border-radius: 4px;
  margin: 10px 0;
  font-size: 14px;
  display: flex;
  align-items: center;
  font-weight: 500;
  
  &::before {
    content: "⚠️";
    margin-right: 8px;
  }
`;

const InputForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  height: 100%;  // 使用全部可用高度
`;

const AccountInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  width: 100%;
  margin-bottom: 20px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  
  strong {
    color: #1a1a1a;
    font-weight: 500;
  }
  
  span {
    color: #4a90e2;
  }

  .nickname {
    color: #28a745;
    font-style: italic;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-left: 20px;  // 与左侧信息保持间距
`;

const ActionButton = styled.button`
  padding: 8px 16px;  // 增加内边距
  border: none;
  border-radius: 6px;  // 增加圆角
  font-size: 14px;    // 增加字体大小
  font-weight: 500;   // 加粗字体
  cursor: pointer;
  transition: all 0.2s;
  min-width: 90px;    // 设置最小宽度
  height: 36px;       // 设置固定高度
  background-color: ${props => {
    if (props.variant === 'success') return '#28a745';
    if (props.danger) return '#dc3545';
    return '#4a90e2';
  }};
  color: white;
  display: flex;      // 使用 flex 布局
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);  // 添加阴影

  &:hover {
    background-color: ${props => {
      if (props.variant === 'success') return '#218838';
      if (props.danger) return '#c82333';
      return '#357abd';
    }};
    transform: translateY(-1px);  // 悬停时轻微上浮
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);  // 点击时恢复位置
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const FamilyGroupSection = styled.div`
  position: relative;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  width: 100%;
  margin-bottom: 15px;
  max-width: 100%;
  max-height: 600px;
  overflow-y: auto;
  
  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
    
    &:hover {
      background: #555;
    }
  }
  
  h3 {
    margin: 0 0 15px 0;
    font-size: 16px;
    color: #333;
    display: flex;
    align-items: center;
    gap: 8px;
    
    span {
      color: #666;
      font-size: 14px;
      font-weight: normal;
    }
  }

  /* 修改内容容器选择器 */
  > div.group-info {  // 只针对家庭组信息的容器
    margin-top: 15px;
  }
`;

const MemberType = styled.div`
  position: absolute;
  left: -1px;
  top: -1px;
  padding: 4px 8px;
  border-radius: 4px 0;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background-color: ${props => {
    switch (props.type) {
      case 'creator':
        return '#1a73e8';  // 创建者蓝色
      case 'current':
        return '#dc3545';  // 当前成员红色
      case 'former':
        return '#dc3545';  // 前任成员红色
      case 'pending':
        return '#28a745';  // 待接受成员绿色
      case 'empty':
        return '#6c757d';  // 空位灰色
      default:
        return '#6c757d';
    }
  }};
`;

const MemberCard = styled.div`
  position: relative;
  background: white;
  padding: 12px;
  padding-top: 30px;
  margin: 8px 0;
  border-radius: 4px;
  border: 1px solid #e1e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MemberInfo = styled.div`
  flex: 1;
`;

const MemberActions = styled.div`
  display: flex;
  gap: 8px;
`;

const InviteTime = styled.div`
  color: #28a745;
  font-size: 13px;
  margin-top: 4px;
`;

const ToastMessage = styled.div`
  position: absolute;
  right: 15px;
  top: 15px;
  padding: 10px 16px;  // 增加内边距
  border-radius: 6px;  // 增加圆角
  font-size: 14px;     // 增加字体大小
  font-weight: 500;
  animation: fadeIn 0.3s ease;
  z-index: 100;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);  // 添加阴影
  
  ${props => props.type === 'success' && `
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `}
  
  ${props => props.type === 'error' && `
    background-color: #ffe6e6;
    color: #dc3545;
    border: 1px solid #f5c6cb;
  `}
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateX(10px); }
    to { opacity: 1; transform: translateX(0); }
  }
`;

// 添加全屏加载组件样式
const FullscreenLoading = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  color: white;
  gap: 20px;
`;

const LoadingText = styled.div`
  font-size: 24px;
  font-weight: 500;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 修改 Steam ID 格式验证函数
const isValidSteamInput = (input) => {
  // Steam 快速邀请链接格式 
  const quickInviteLinkPattern = /^https:\/\/s\.team\/p\/[a-zA-Z0-9-]+\/[a-zA-Z0-9-]+\/?$/;
  
  const isValid = quickInviteLinkPattern.test(input); // 只验证快速邀请链接

  return {
    valid: isValid,
    error: isValid ? null : '请输入正确的快速邀请链接'
  };
};

function Home() {
  const { user, logout } = useAuth();
  const [steamAccount, setSteamAccount] = useState('');
  const [logs, setLogs] = useState(() => {
    const savedLogs = localStorage.getItem('operationLogs');
    return savedLogs ? JSON.parse(savedLogs) : [];
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [steamLoginInfo, setSteamLoginInfo] = useState(() => {
    const saved = localStorage.getItem('steamLoginInfo');
    return saved ? JSON.parse(saved) : null;
  });
  const [newMemberSteamId, setNewMemberSteamId] = useState('');
  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const [isLoggingOut, setIsLoggingOut] = useState(false);  // 添加登出状态
  const [isRestarting, setIsRestarting] = useState(false);
  const [restartCountdown, setRestartCountdown] = useState(0);
  const [isProcessingQuickInvite, setIsProcessingQuickInvite] = useState(false);
  const [shouldRemoveFriend, setShouldRemoveFriend] = useState(true); // 添加是否移除好友的状态
  const [autoLogoutAfterInvite, setAutoLogoutAfterInvite] = useState(false); // 添加邀请成功后自动注销状态
  const navigate = useNavigate();

  useEffect(() => {
    if (steamLoginInfo) {
      localStorage.setItem('steamLoginInfo', JSON.stringify(steamLoginInfo));
    } else {
      localStorage.removeItem('steamLoginInfo');
    }
  }, [steamLoginInfo]);

  useEffect(() => {
    if (steamLoginInfo) {
      const checkSteamStatus = async () => {
        try {
          const response = await axios.post('/api/steam/status', {
            steamid: steamLoginInfo.steamid
          });
          if (!response.data.success) {
            setSteamLoginInfo(null);
          }
        } catch (error) {
          setSteamLoginInfo(null);
        }
      };
      checkSteamStatus();
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('operationLogs', JSON.stringify(logs));
  }, [logs]);

  const handleLogout = async () => {
    try {
      if (window.cookieWindow) {
        window.cookieWindow.close();
      }
      await logout();
    } catch (error) {
      console.error('登出请求失败，但已清除本地状态');
    }
  };

  const handleSteamLogout = async () => {
    try {
        setIsLoggingOut(true);  // 开始登出
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, `[${timestamp}] 正在登出 Steam 账号: ${steamLoginInfo.steamid}`]);

        if (window.cookieWindow) {
            window.cookieWindow.close();
        }

        setToast({
            message: '正在登出 Steam...',
            type: 'success',
            visible: true
        });

        const response = await axios.post('/api/steam/logout', {
            steamid: steamLoginInfo.steamid
        });

        if (response.data.success) {
            setLogs(prev => [...prev, `[${timestamp}] Steam 账号已登出`]);
            
            // 等待 1 秒确保后端完成清理
            setTimeout(() => {
                setSteamLoginInfo(null);
                setIsLoggingOut(false);  // 结束登出
                setToast({
                    message: '已成功登出 Steam',
                    type: 'success',
                    visible: true
                });
                setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 2000);
            }, 1000);
        }
    } catch (error) {
        setIsLoggingOut(false);  // 登出失败
        const errorMsg = error.response?.data?.error || '登出失败';
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, `[${timestamp}] 错误: ${errorMsg}`]);
        setError(errorMsg);
        
        setToast({
            message: '登出失败: ' + errorMsg,
            type: 'error',
            visible: true
        });
        setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!steamAccount.trim()) return;

    setIsSubmitting(true);
    setError('');
    try {
      const timestamp = new Date().toLocaleTimeString();
      setLogs(prev => [...prev, `[${timestamp}] 正在查询账号: ${steamAccount}`]);

      const response = await axios.post('/api/steam/query', {
        steamid: steamAccount
      });

      if (response.data.success) {
        const { steamid, field, status, nickname, familyGroup } = response.data.data;
        console.log('Login response:', response.data);
        console.log('Family group data:', familyGroup);
        console.log('Former members:', familyGroup.formerMembers);
        
        setLogs(prev => [...prev, `[${timestamp}] Steam 登录成功: ${steamid} (${field})`]);
        if (!familyGroup) {
          setLogs(prev => [...prev, `[${timestamp}] 警告: 未获取到家庭组信息`]);
        }
        setSteamLoginInfo({ steamid, field, status, nickname, familyGroup });
        setSteamAccount('');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || '查询失败';
      const errorCode = error.response?.data?.code;
      const timestamp = new Date().toLocaleTimeString();
      
      if (errorMsg.includes('超时')) {
        setLogs(prev => [...prev, `[${timestamp}] 错误: Steam 服务器响应超时，请稍后重试`]);
      } else {
        setLogs(prev => [...prev, `[${timestamp}] 错误: ${errorMsg}`]);
      }
      
      setError(errorMsg);
      setSteamLoginInfo(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (steamid) => {
    try {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, `[${timestamp}] 正在移除成员: ${steamid}`]);

        const response = await axios.post('/api/steam/remove-member', {
            steamid: steamLoginInfo.steamid,
            targetSteamId: steamid
        });

        if (response.data.success) {
            setLogs(prev => [...prev, `[${timestamp}] 成功移除成员`]);
            setSteamLoginInfo(prev => ({
                ...prev,
                familyGroup: response.data.data.familyGroup
            }));
            setToast({
                message: '成功移除成员',
                type: 'success',
                visible: true
            });
            setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
        }
    } catch (error) {
        handleSteamClientError(error);
    }
  };

  const handleRemoveFormerMember = (steamid) => {
    // Implementation of handleRemoveFormerMember
  };

  const handleCancelInvite = async (steamid) => {
    try {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, `[${timestamp}] 正在取消邀请: ${steamid}`]);

        const response = await axios.post('/api/steam/cancel-invite', {
            steamid: steamLoginInfo.steamid,
            targetSteamId: steamid
        });

        if (response.data.success) {
            setLogs(prev => [...prev, `[${timestamp}] 成功取消邀请`]);
            setSteamLoginInfo(prev => ({
                ...prev,
                familyGroup: response.data.data.familyGroup
            }));
            setToast({
                message: '成功取消邀请',
                type: 'success',
                visible: true
            });
            setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
        }
    } catch (error) {
        handleSteamClientError(error);
    }
  };

  const handleInviteMember = async () => {
    const input = newMemberSteamId.trim();
    
    if (!input) {
        setToast({
            message: '请输入快速邀请链接',
            type: 'error',
            visible: true
        });
        setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
        return;
    }

    // 检查是否是快速邀请链接
    const isQuickInviteLink = input.includes('s.team/p/');
    if (!isQuickInviteLink) {
        setToast({
            message: '请输入正确的快速邀请链接',
            type: 'error',
            visible: true
        });
        setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
        return;
    }

    const timestamp = new Date().toLocaleTimeString();

    try {
        // 处理快速邀请链接
        setIsProcessingQuickInvite(true);
        setLogs(prev => [...prev, `[${timestamp}] 正在检查快速邀请链接有效性: ${input}`]);

                        // 检查链接有效性
        const checkResponse = await axios.post('/api/steam/check-quick-invite', {
            steamid: steamLoginInfo.steamid,
            link: input
        });

        if (checkResponse.data.success && checkResponse.data.data.valid) {
            const targetSteamId = checkResponse.data.data.steamid;
            setLogs(prev => [...prev, `[${timestamp}] 快速邀请链接有效，目标steamid【${targetSteamId}】正在添加好友并邀请`]);

            // 兑换快速邀请链接，添加好友，然后邀请加入家庭组
            const redeemResponse = await axios.post('/api/steam/redeem-quick-invite', {
            steamid: steamLoginInfo.steamid,
                link: input,
                shouldRemoveFriend: shouldRemoveFriend // 添加是否移除好友的参数
            });

            if (redeemResponse.data.success) {
                const friendSteamID = redeemResponse.data.data.steamid;
                setLogs(prev => [...prev, `[${timestamp}] 成功处理快速邀请链接并邀请用户: ${friendSteamID}`]);
                
                // 检查是否已经是好友
                if (redeemResponse.data.data.alreadyFriend) {
                    setLogs(prev => [...prev, `[${timestamp}] 用户已经是好友，跳过添加好友步骤`]);
                }
                
                // 根据是否应该移除好友以及移除状态记录日志
                if (shouldRemoveFriend) {
                    if (redeemResponse.data.data.removeFriendSuccess) {
                        setLogs(prev => [...prev, `[${timestamp}] 成功移除好友: ${friendSteamID}`]);
                    } else {
                        const errorMsg = redeemResponse.data.data.removeFriendError || '未知错误';
                        setLogs(prev => [...prev, `[${timestamp}] 移除好友失败: ${errorMsg}`]);
                    }
                } else {
                    setLogs(prev => [...prev, `[${timestamp}] 已跳过移除好友`]);
                }
                
                setNewMemberSteamId('');
                
                // 更新家庭组信息
            setSteamLoginInfo(prev => ({
                ...prev,
                    familyGroup: redeemResponse.data.data.familyGroup
            }));

            setToast({
                message: '邀请发送成功',
                type: 'success',
                visible: true
            });
            setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
            
            // 如果启用了自动注销，则在邀请成功后注销Steam
            if (autoLogoutAfterInvite) {
                setLogs(prev => [...prev, `[${timestamp}] 邀请成功，正在根据设置自动注销Steam`]);
                setTimeout(() => {
                    handleSteamLogout();
                }, 1000); // 延迟1秒后执行注销
            }
        }
        } else {
            setLogs(prev => [...prev, `[${timestamp}] 快速邀请链接已失效`]);
            setToast({
                message: '快速邀请链接已失效',
                type: 'error',
                visible: true
            });
            setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
        }
        setIsProcessingQuickInvite(false);
    } catch (error) {
        setIsProcessingQuickInvite(false);
        handleSteamClientError(error);
    }
  };

  const handleSteamIdInput = async (value) => {
    setNewMemberSteamId(value);
    
    if (!value.trim()) {
      setError('');
      return;
    }
    
    // 不再需要处理个人资料链接
  };

  const clearLogs = () => {
    setLogs([]);
    localStorage.removeItem('operationLogs');
  };

  // 修改 getInviteTime 函数
  const getInviteTime = (steamid) => {
    // 反转日志数组以便从最新的开始查找
    const reversedLogs = [...logs].reverse();
    
    // 查找最新的邀请记录 (支持两种格式)
    const inviteLog = reversedLogs.find(log => 
      log.includes(`正在邀请成员: ${steamid}`) || 
      log.includes(`成功处理快速邀请链接并邀请用户: ${steamid}`)
    );
    
    if (inviteLog) {
      // 提取时间戳 [HH:MM:SS]
      const timeMatch = inviteLog.match(/\[(.*?)\]/);
      if (timeMatch) {
        // 获取当前日期
        const today = new Date();
        const [hours, minutes, seconds] = timeMatch[1].split(':').map(Number);  // 转换为数字
        
        // 创建邀请时间
        const inviteTime = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          hours || 0,      // 如果解析失败则使用 0
          minutes || 0,
          seconds || 0
        );

        // 检查是否是有效日期
        if (isNaN(inviteTime.getTime())) {
          console.error('Invalid date components:', { hours, minutes, seconds });
          return null;
        }

        return inviteTime.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).replace(/\//g, '-');
      }
    }
    return null;
  };

  // 移动 handleSteamClientError 函数到组件内部
  const handleSteamClientError = (error) => {
    const errorMsg = error.response?.data?.error || '操作失败';
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] 错误: ${errorMsg}`]);
    
    if (errorMsg.includes('未找到 Steam 客户端')) {
      setToast({
        message: 'Steam 连接已断开，正在重新连接...',
        type: 'error',
        visible: true
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      setToast({
        message: errorMsg,
        type: 'error',
        visible: true
      });
      setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
    }
  };

  // 修改重启系统按钮的处理函数
  const handleSystemRestart = async () => {
    try {
      setIsRestarting(true);
      setRestartCountdown(5);

      // 发送重启命令
      await axios.post('/api/system/restart');
      
      // 直接开始倒计时，不等待响应
      const timer = setInterval(() => {
        setRestartCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            // 重定向到登录页面
            window.location.href = '/login';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (error) {
      // 即使请求失败也继续倒计时
      console.error('重启请求发送失败，但继续执行重定向');
      
      const timer = setInterval(() => {
        setRestartCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = '/login';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  return (
    <HomeContainer>
      <ContentWrapper>
        <Header>
          <Title>
            Steam Family 共享管理 v2.4
            <UserInfo>当前用户：{user?.username}</UserInfo>
          </Title>
          <div style={{ display: 'flex', gap: '10px' }}>
            <ActionButton 
              onClick={() => {
                const url = process.env.NODE_ENV === 'production'
                  ? 'https://steamfamilyweb.tginot.com/api/cookie'
                  : 'http://localhost:3001/api/cookie';
                window.cookieWindow = window.open(url, '_blank');
              }}
              style={{ backgroundColor: '#28a745' }}
            >
              查看 Cookie
            </ActionButton>
            <ActionButton 
              onClick={() => window.open('https://store.steampowered.com/account/', '_blank')}
              style={{ backgroundColor: '#4a90e2' }}
            >
              访问 Steam
            </ActionButton>
            <ActionButton 
              onClick={handleSystemRestart}
              disabled={isRestarting}
              style={{ backgroundColor: '#ffc107', color: '#000' }}
            >
              {isRestarting ? '重启中...' : '重启系统'}
            </ActionButton>
            <LogoutButton onClick={handleLogout}>退出登录</LogoutButton>
          </div>
        </Header>

        <MainContent>
          <InputSection>
            <InputForm onSubmit={handleSubmit}>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {steamLoginInfo ? (
                <>
                  <AccountInfo>
                    <InfoContainer>
                      <InfoRow>
                        <strong>当前登录账号：</strong>
                        <span>{steamLoginInfo.steamid}</span>
                        {steamLoginInfo.nickname && (
                          <span className="nickname">({steamLoginInfo.nickname})</span>
                        )}
                      </InfoRow>
                      <InfoRow>
                        <strong>账号所属：</strong>
                        <span>{steamLoginInfo.field}</span>
                      </InfoRow>
                    </InfoContainer>
                    <ButtonGroup>
                      <ActionButton 
                        type="button" 
                        onClick={handleSteamLogout}
                        disabled={isLoggingOut}
                      >
                        {isLoggingOut ? '正在注销...' : '注销 Steam'}
                      </ActionButton>
                    </ButtonGroup>
                  </AccountInfo>

                  {steamLoginInfo.familyGroup ? (
                    <FamilyGroupSection>
                      <h3>
                        家庭组信息
                        <span style={{ 
                          color: '#666', 
                          fontSize: '14px', 
                          fontWeight: 'normal',
                          marginLeft: '8px' 
                        }}>
                          (ID: {steamLoginInfo.familyGroup.groupId})
                        </span>
                      </h3>
                      {toast.visible && (
                        <ToastMessage type={toast.type}>
                          {toast.message}
                        </ToastMessage>
                      )}
                      <div className="group-info">
                        <strong>可用空位：</strong>
                        {steamLoginInfo.familyGroup.freeSpots}
                        {steamLoginInfo.familyGroup.cancelableSpots > 0 && (
                          <span style={{ color: '#666', marginLeft: '5px' }}>
                            （{steamLoginInfo.familyGroup.cancelableSpots}可取消）
                          </span>
                        )}
                        {steamLoginInfo.familyGroup.slotCooldownSeconds > 0 && (
                          <div style={{ 
                            color: '#e67e22', 
                            fontSize: '13px', 
                            marginTop: '5px',
                            padding: '4px 8px',
                            backgroundColor: '#fff3e0',
                            borderRadius: '4px',
                            display: 'inline-block'
                          }}>
                            槽位冷却中：{steamLoginInfo.familyGroup.slotCooldown}
                            （前任成员占用 {steamLoginInfo.familyGroup.formerMembers?.length || 0} 个槽位）
                          </div>
                        )}
                      </div>

                      {steamLoginInfo.familyGroup.freeSpots > 0 && (
                        <MemberCard>
                          <MemberType type="empty">空位</MemberType>
                          <MemberInfo>
                            <Input
                              type="text"
                              value={newMemberSteamId}
                              onChange={(e) => handleSteamIdInput(e.target.value)}
                              placeholder="输入格式(https://s.team/p/...)"
                            />
                            {newMemberSteamId && !isValidSteamInput(newMemberSteamId).valid && (
                              <div style={{ 
                                color: '#dc3545', 
                                fontSize: '12px', 
                                marginTop: '4px' 
                              }}>
                                {isValidSteamInput(newMemberSteamId).error}
                              </div>
                            )}
                            <div style={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                              marginTop: '8px',
                              fontSize: '14px',
                              color: '#666'
                            }}>
                              <div>
                                <input 
                                  type="checkbox" 
                                  id="removeFriendToggle"
                                  checked={shouldRemoveFriend}
                                  onChange={(e) => setShouldRemoveFriend(e.target.checked)}
                                  style={{ marginRight: '8px' }}
                                />
                                <label htmlFor="removeFriendToggle">邀请成功后移除好友</label>
                              </div>
                              <div>
                                <input 
                                  type="checkbox" 
                                  id="autoLogoutToggle"
                                  checked={autoLogoutAfterInvite}
                                  onChange={(e) => setAutoLogoutAfterInvite(e.target.checked)}
                                  style={{ marginRight: '8px' }}
                                />
                                <label htmlFor="autoLogoutToggle">邀请成功后自动注销Steam</label>
                              </div>
                            </div>
                          </MemberInfo>
                          <MemberActions>
                            <ActionButton 
                              onClick={handleInviteMember}
                              disabled={isProcessingQuickInvite || !isValidSteamInput(newMemberSteamId).valid}
                              style={{ 
                                opacity: isProcessingQuickInvite ? 0.6 : 1,
                                cursor: isProcessingQuickInvite ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {isProcessingQuickInvite ? '处理中...' : '邀请'}
                            </ActionButton>
                          </MemberActions>
                        </MemberCard>
                      )}

                      {Array.isArray(steamLoginInfo.familyGroup.pendingInvites) && 
                        steamLoginInfo.familyGroup.pendingInvites.length > 0 && 
                        steamLoginInfo.familyGroup.pendingInvites.map(invite => {
                          const inviteTime = getInviteTime(invite.steamid);
                          return (
                            <MemberCard key={invite.steamid}>
                              <MemberType type="pending">待接受</MemberType>
                              <MemberInfo>
                                <div>Steam ID: {invite.steamid}</div>
                                <div>角色: {invite.role}</div>
                                <div>状态: 待接受</div>
                                {inviteTime && (
                                  <InviteTime>
                                    邀请时间: {inviteTime}
                                  </InviteTime>
                                )}
                              </MemberInfo>
                              <MemberActions>
                                <ActionButton 
                                  variant="success"
                                  onClick={() => handleCancelInvite(invite.steamid)}
                                >
                                  取消邀请
                                </ActionButton>
                              </MemberActions>
                            </MemberCard>
                          );
                        })
                      }

                      {Array.isArray(steamLoginInfo.familyGroup.members) && 
                        steamLoginInfo.familyGroup.members.map((member, index) => (
                          <MemberCard key={member.steamid}>
                            <MemberType type={index === 0 ? 'creator' : 'current'}>
                              {index === 0 ? '创建者' : '当前成员'}
                            </MemberType>
                            <MemberInfo>
                              <div>Steam ID: {member.steamid}</div>
                              <div>角色: {member.role}</div>
                              <div>加入时间: {member.joinTime}</div>
                              <div>冷却时间: {member.cooldown}</div>
                            </MemberInfo>
                            {index !== 0 && (
                              <MemberActions>
                                <ActionButton 
                                  danger 
                                  onClick={() => handleRemoveMember(member.steamid)}
                                >
                                  移除
                                </ActionButton>
                              </MemberActions>
                            )}
                          </MemberCard>
                        ))
                      }

                      {Array.isArray(steamLoginInfo.familyGroup.formerMembers) && 
                        steamLoginInfo.familyGroup.formerMembers.length > 0 && 
                        steamLoginInfo.familyGroup.formerMembers.map(member => (
                          <MemberCard key={member.steamid}>
                            <MemberType type="former">前任成员</MemberType>
                            <MemberInfo>
                              <div>Steam ID: {member.steamid}</div>
                              <div>状态: 前任成员 {steamLoginInfo.familyGroup.slotCooldownSeconds > 0 
                                ? <span style={{ color: '#e67e22', fontWeight: 500 }}>（冷却中，占用槽位）</span>
                                : <span style={{ color: '#28a745', fontWeight: 500 }}>（冷却已结束，不占用槽位）</span>
                              }</div>
                            </MemberInfo>
                          </MemberCard>
                        ))
                      }
                    </FamilyGroupSection>
                  ) : steamLoginInfo ? (
                    <div style={{ padding: '15px', color: '#666' }}>
                      正在获取家庭组信息...
                    </div>
                  ) : null}
                </>
              ) : (
                <InputGroup>
                  <InputWrapper>
                    <Label>Steam 账号</Label>
                    <Input
                      type="text"
                      value={steamAccount}
                      onChange={(e) => setSteamAccount(e.target.value)}
                      placeholder="请输入您的 Steam 账号"
                      disabled={isSubmitting}
                    />
                  </InputWrapper>
                  <SubmitButton type="submit" disabled={isSubmitting || !steamAccount.trim()}>
                    {isSubmitting ? '登录中...' : '登录'}
                  </SubmitButton>
                </InputGroup>
              )}
            </InputForm>
          </InputSection>

          <LogSection>
            <LogHeader>
              <LogTitle>操作日志</LogTitle>
              <ClearButton onClick={clearLogs}>
                清空日志
              </ClearButton>
            </LogHeader>
            <LogContent>
              {[...logs].reverse().map((log, index) => (
                <LogEntry key={index}>{log}</LogEntry>
              ))}
            </LogContent>
          </LogSection>
        </MainContent>
      </ContentWrapper>

      {isRestarting && (
        <FullscreenLoading>
          <LoadingSpinner />
          <LoadingText>系统正在重启中...</LoadingText>
          <LoadingText>页面将在 {restartCountdown} 秒后刷新</LoadingText>
        </FullscreenLoading>
      )}
    </HomeContainer>
  );
}

export default Home; 